import { useEffect, useState } from 'react';
import { AlertTriangle, Eye, Lock, RefreshCw, X } from '../../components/icons';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { DataTable, type Column } from '../../components/data-table';
import { StatusBadge } from '../../components/status-badge';
import { SkeletonRows } from '../../components/skeleton';
import { PageHeader } from '../../components/page-header';
import { Modal } from '../../components/modal';
import { extractErr } from '../../api/error-message';
import { useDebouncedValue } from '../../components/table-search';
import { useUrlTab } from '../../services/attention';

// Wired to GET /admin/orders (+ detail GET /admin/orders/:id and per-item
// force-status POST /admin/order-items/:id/status). Reflects the new fulfilment
// vocabulary: PLACED → ACCEPTED → PACKED → READY_FOR_DELIVERY → PICKED_UP →
// DELIVERED (SHIPPED for the courier fallback).

// Server-side paging, 15 rows a page — the platform-wide listing size.
const PER_PAGE = 15;

// Parent-order status tabs (deriveOrderStatus vocabulary).
const STATUS_TABS = ['ALL', 'PLACED', 'PROCESSING', 'SHIPPED', 'DELIVERED', 'CANCELLED'] as const;
type StatusKey = (typeof STATUS_TABS)[number];

// Which targets an item may be forced to comes from the API (`allowedTargets`
// on each detail item, from ADMIN_TRANSITIONS in order.service) — the console
// keeps no copy of the whitelist. This is only the explanation shown in the
// confirmation for each target.
const CONSEQUENCE: Record<string, string> = {
  ACCEPTED: 'Marks the item accepted on the seller’s behalf.',
  PACKED: 'Marks the item packed without the seller’s packing photo.',
  READY_FOR_DELIVERY: 'Publishes the seller’s slice to the delivery pool once all of it is ready.',
  DELIVERED: 'Releases the seller’s escrowed payout. Requires a captured payment and cannot be undone.',
  REJECTED: 'Returns the stock, cancels the seller’s payout and queues a refund on the Refunds page.',
  CANCELLED: 'Returns the stock, cancels the seller’s payout, detaches the item from its delivery and queues a refund on the Refunds page.',
};

// Mirrors OTP_MAX_ATTEMPTS in shipping/assignment.service.
const OTP_MAX_ATTEMPTS = 5;

interface OrderRow {
  id: string;
  orderNumber: string;
  status: string;
  placedAt: string | null;
  paymentMethod: string;
  totalAmount: string;
  customer: { id: string; fullName: string; phoneE164: string } | null;
  _count: { items: number };
}

interface OrderItem {
  id: string;
  itemStatus: string;
  qty: number;
  lineSubtotal: string;
  productSnapshot: { title?: string } | null;
  seller: { businessName: string } | null;
  /** Statuses an admin may force this item to (absent on older API builds). */
  allowedTargets?: string[];
}

interface OrderDetail extends OrderRow {
  items: OrderItem[];
  payments: { id: string; method: string; status: string; amount: string }[];
  deliveryAssignments?: { id: string; status: string; otpFailedAttempts: number }[];
}

function money(v: string | number): string {
  return `₹${Number(v).toLocaleString('en-IN')}`;
}

// Display only — sentence case (`READY_FOR_DELIVERY` → `Ready for delivery`).
function prettyStatus(s: string): string {
  const lower = s.replace(/_/g, ' ').toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

export function OrdersPage() {
  // In the URL (?tab=) so the dashboard can open a specific queue.
  const [tab, setTab] = useUrlTab<StatusKey>(STATUS_TABS, 'ALL');
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  // Only the very first fetch shows the skeleton. Later fetches (typing in the
  // search, paging) keep the table mounted so the search box keeps its focus.
  const [loaded, setLoaded] = useState(false);
  const [selected, setSelected] = useState<OrderDetail | null>(null);
  // Server-side search across every column the table shows (order number,
  // customer name / phone, amount, status, payment, date) — the list is paged
  // by the API, so filtering the 15 loaded rows would miss the other pages.
  const [search, setSearch] = useState(() => new URLSearchParams(window.location.search).get('q') ?? '');
  const q = useDebouncedValue(search.trim(), 300);
  const [reloadKey, setReloadKey] = useState(0);
  const load = () => setReloadKey((k) => k + 1);

  // A new tab or query is a new result set — go back to page 1 rather than
  // landing on a page number that may not exist in the filtered set.
  useEffect(() => { setPage(1); }, [tab, q]);

  // Guard against out-of-order responses when the tab, query or page changes quickly.
  useEffect(() => {
    let alive = true;
    api
      .get<{ items: OrderRow[]; total: number }>('/admin/orders', {
        params: {
          ...(tab === 'ALL' ? {} : { status: tab }),
          ...(q ? { q } : {}),
          page,
          limit: PER_PAGE,
        },
      })
      .then(({ data }) => { if (alive) { setRows(data.items); setTotal(data.total); } })
      .catch((err) => { if (alive) toast.error(extractErr(err)); })
      .finally(() => { if (alive) setLoaded(true); });
    return () => { alive = false; };
  }, [tab, q, page, reloadKey]);

  const openDetail = async (r: OrderRow) => {
    try {
      const { data } = await api.get<OrderDetail>(`/admin/orders/${r.id}`);
      setSelected(data);
    } catch (err) {
      toast.error(extractErr(err));
    }
  };

  const columns: Column<OrderRow>[] = [
    {
      key: 'orderNumber',
      label: 'Order',
      render: (r) => <span className="sku font-semibold text-ink-900">{r.orderNumber}</span>,
    },
    {
      key: 'customer',
      label: 'Customer',
      render: (r) => (
        <div>
          <p className="font-medium text-ink-900">{r.customer?.fullName ?? 'Customer'}</p>
          <p className="text-[12.5px] text-ink-500">{r.customer?.phoneE164 ?? ''}</p>
        </div>
      ),
    },
    { key: '_count', label: 'Items', render: (r) => `${r._count.items} item${r._count.items > 1 ? 's' : ''}` },
    {
      key: 'totalAmount',
      label: 'Amount',
      align: 'right',
      render: (r) => <span className="font-tabular font-semibold text-ink-900">{money(r.totalAmount)}</span>,
    },
    { key: 'status', label: 'Status', render: (r) => <StatusBadge status={prettyStatus(r.status)} /> },
    { key: 'paymentMethod', label: 'Payment' },
    {
      key: 'placedAt',
      label: 'Date',
      render: (r) => (r.placedAt ? new Date(r.placedAt).toLocaleDateString('en-IN') : '—'),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Orders"
        description="All marketplace orders across sellers"
        actions={
          <button type="button" onClick={load} className="btn btn-secondary">
            <RefreshCw size={16} /> Refresh
          </button>
        }
      />

      <div className="flex flex-wrap gap-2">
        {STATUS_TABS.map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            aria-pressed={tab === t}
            className="fchip"
          >
            {t === 'ALL' ? 'All' : prettyStatus(t)}
          </button>
        ))}
      </div>

      {!loaded ? (
        <div className="panel overflow-hidden">
          <SkeletonRows cols={6} />
        </div>
      ) : (
        <DataTable
          columns={columns}
          data={rows}
          rowKey={(r) => r.id}
          perPage={PER_PAGE}
          serverPagination={{ page, totalItems: total, onPageChange: setPage }}
          searchPlaceholder="Search by order, customer, phone, amount, status, payment or date"
          searchValue={search}
          onSearchChange={setSearch}
          actions={(r) => (
            <div className="flex items-center justify-end gap-1.5">
              <button
                type="button"
                title="View details & override status"
                onClick={() => void openDetail(r)}
                aria-label="View details & override status"
                className="btn btn-quiet btn-icon btn-sm"
              >
                <Eye size={18} />
              </button>
            </div>
          )}
        />
      )}

      {selected && (
        <OrderDetailModal
          order={selected}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            const { data } = await api.get<OrderDetail>(`/admin/orders/${selected.id}`);
            setSelected(data);
            load();
          }}
        />
      )}
    </div>
  );
}

function OrderDetailModal({
  order,
  onClose,
  onChanged,
}: {
  order: OrderDetail;
  onClose: () => void;
  onChanged: () => Promise<void>;
}) {
  const [busyId, setBusyId] = useState<string | null>(null);
  // A pending override, shown as a from → to confirmation before anything is
  // sent. The old <select onChange> fired the moment an option was picked.
  const [pending, setPending] = useState<{ item: OrderItem; to: string } | null>(null);
  const [note, setNote] = useState('');

  const force = async () => {
    if (!pending) return;
    const { item, to } = pending;
    setBusyId(item.id);
    try {
      await api.post(`/admin/order-items/${item.id}/status`, { itemStatus: to, note: note.trim() || 'Admin override' });
      toast.success(`Item set to ${prettyStatus(to)}`);
      setPending(null);
      setNote('');
      await onChanged();
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setBusyId(null);
    }
  };

  const resetOtp = async (assignmentId: string) => {
    try {
      await api.post(`/admin/delivery-assignments/${assignmentId}/reset-otp`);
      toast.success('OTP lock cleared — the partner can enter the code again');
      await onChanged();
    } catch (err) {
      toast.error(extractErr(err));
    }
  };

  const lockedAssignments = (order.deliveryAssignments ?? []).filter(
    (a) => a.status === 'PICKED_UP' && a.otpFailedAttempts >= OTP_MAX_ATTEMPTS,
  );

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-scrim" onClick={onClose}>
      <div
        className="flex h-full w-[min(560px,100vw)] flex-col bg-surface-card shadow-elev-4"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-surface-line px-6 pb-4 pt-5">
          <div className="min-w-0">
            <h2 className="font-display text-drawer-title text-ink-900">Order details</h2>
            <p className="sku mt-1 font-semibold text-ink-900">{order.orderNumber}</p>
            <p className="mt-0.5 text-[13.5px] text-ink-500">
              {order.customer?.fullName} · <span className="font-tabular font-semibold text-ink-700">{money(order.totalAmount)}</span> · {order.paymentMethod}
            </p>
            <div className="mt-2"><StatusBadge status={prettyStatus(order.status)} /></div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="btn btn-quiet btn-icon btn-sm -mr-2">
            <X size={20} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-6 py-5">
          {lockedAssignments.map((a) => (
            <div key={a.id} className="alert alert-warning mb-4 items-start">
              <Lock size={18} className="mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold">Delivery locked — too many wrong OTPs</p>
                <p className="text-[13.5px]">
                  Confirm with the customer that the partner is at the door before unlocking.
                </p>
              </div>
              <button
                type="button"
                onClick={() => void resetOtp(a.id)}
                className="btn btn-secondary btn-sm"
              >
                Unlock
              </button>
            </div>
          ))}

          <h3 className="mb-3 text-[16px] font-semibold text-ink-900">Items — override status</h3>
          <div className="space-y-3">
            {order.items.map((it) => {
              const targets = it.allowedTargets ?? [];
              return (
                <div key={it.id} className="card p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-medium text-ink-900">{it.productSnapshot?.title ?? 'Item'}</p>
                      <p className="text-[12.5px] text-ink-500">
                        {it.seller?.businessName ?? 'Seller'} · Qty {it.qty} · <span className="font-tabular font-semibold">{money(it.lineSubtotal)}</span>
                      </p>
                    </div>
                    <StatusBadge status={prettyStatus(it.itemStatus)} />
                  </div>
                  <div className="mt-3 flex items-center gap-2.5">
                    {targets.length === 0 ? (
                      <span className="text-[12.5px] text-ink-500">
                        {it.itemStatus === 'DELIVERED'
                          ? 'Delivered — cannot be changed (the seller has been paid)'
                          : 'Closed — no further status changes'}
                      </span>
                    ) : (
                      <>
                        <select
                          value=""
                          disabled={busyId === it.id}
                          onChange={(e) => {
                            if (e.target.value) setPending({ item: it, to: e.target.value });
                          }}
                          className="input input-sm w-auto min-w-[200px]"
                        >
                          <option value="">Override status…</option>
                          {targets.map((s) => (
                            <option key={s} value={s}>{prettyStatus(s)}</option>
                          ))}
                        </select>
                        <span className="text-[12.5px] text-ink-500">Asks for confirmation first</span>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {order.payments?.length > 0 && (
            <>
              <h3 className="mb-3 mt-6 text-[16px] font-semibold text-ink-900">Payments</h3>
              <div className="space-y-2">
                {order.payments.map((p) => (
                  <div key={p.id} className="card flex items-center justify-between p-4 text-[13.5px]">
                    <span className="text-ink-700">
                      {p.method} · <span className="font-tabular font-semibold text-ink-900">{money(p.amount)}</span>
                    </span>
                    <StatusBadge status={prettyStatus(p.status)} />
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {pending && (
        // Stop clicks inside the confirmation reaching the drawer backdrop,
        // which would close the whole order panel.
        <div onClick={(e) => e.stopPropagation()}>
          <Modal
            title={
              <span className="flex items-center gap-2">
                <AlertTriangle size={20} className="text-warning-ink" />
                Override item status?
              </span>
            }
            onClose={() => setPending(null)}
            busy={busyId != null}
            footer={
              <>
                <button
                  type="button"
                  disabled={busyId != null}
                  onClick={() => setPending(null)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={busyId != null}
                  onClick={() => void force()}
                  className="btn btn-primary"
                >
                  {busyId ? 'Applying…' : `Set to ${prettyStatus(pending.to).toLowerCase()}`}
                </button>
              </>
            }
          >
            <p className="text-[14px] font-medium text-ink-900">{pending.item.productSnapshot?.title ?? 'Item'}</p>
            <div className="my-3 flex items-center gap-2">
              <StatusBadge status={prettyStatus(pending.item.itemStatus)} />
              <span className="text-ink-400">→</span>
              <StatusBadge status={prettyStatus(pending.to)} />
            </div>
            <p className="mb-3 text-[13.5px] text-ink-700">
              {CONSEQUENCE[pending.to] ?? 'This bypasses the seller workflow.'}
            </p>
            <label className="field-label mb-1.5" htmlFor="override-note">Reason</label>
            <textarea
              id="override-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={500}
              rows={2}
              placeholder="Recorded in the audit log"
              className="input"
            />
          </Modal>
        </div>
      )}
    </div>
  );
}
